import React, { useState, useEffect } from 'react';
import './VillainEncounter.css';
import { soundManager } from '../utils/SoundManager';

const VillainEncounter = ({ villain, battleStats, repCount, onVillainComplete, onBack }) => {
    const [currentPhase, setCurrentPhase] = useState('intro'); // intro, move-selection, action-selection, battle-result
    const [selectedMove, setSelectedMove] = useState(null);
    const [selectedAction, setSelectedAction] = useState(null);
    const [battleResult, setBattleResult] = useState(null);
    const [showContinueButton, setShowContinueButton] = useState(false);
    const [overlayOpacity, setOverlayOpacity] = useState(0.9); // Start with strong overlay

    // Play villain sound on encounter and fade overlay
    useEffect(() => {
        // 🎵 VILLAIN APPEARS SOUND!
        soundManager.playSound('villain', 0.8);
        console.log(`💀 ${villain.name} has appeared!`);

        // Start fading the overlay after 500ms to reveal the villain
        setTimeout(() => {
            setOverlayOpacity(0); // Fade to lighter overlay
        }, 500);

        // Move to next phase
        setTimeout(() => setCurrentPhase('move-selection'), 2000);
    }, [villain]);

    // Reset overlay when phase changes (optional - you can remove this if you want it to stay faded)
    useEffect(() => {
        if (currentPhase === 'move-selection') {
            setOverlayOpacity(0); // Slightly more visible for gameplay
        } else if (currentPhase === 'action-selection' || currentPhase === 'battle-result') {
            setOverlayOpacity(0); // Even more visible for combat
        }
    }, [currentPhase]);

    // Handle move selection
    const handleMoveSelection = (move, moveIndex) => {
        // 🎵 BUTTON CLICK SOUND
        soundManager.playButtonSound();

        setSelectedMove({ move, moveIndex });
        setCurrentPhase('action-selection');
    };

    // Handle action selection and battle calculation
    const handleActionSelection = (action) => {
        // 🎵 PLAY ACTION SOUND
        switch(action) {
            case 'attack':
                soundManager.playSound('attack', 0.7);
                break;
            case 'defend':
                soundManager.playSound('defend', 0.7);
                break;
            case 'run':
                soundManager.playSound('run', 0.7);
                break;
        }

        setSelectedAction(action);

        // Calculate battle result
        const moveWeights = villain.weights[selectedMove.moveIndex];
        const actionMultiplier = getActionMultiplier(action);

        // Apply move effects
        let attackChange = moveWeights.attackValue || 0;
        let defendChange = moveWeights.defendValue || 0;
        let workoutChange = moveWeights.workoutValue || 0;

        // Apply action effects (randomized)
        const randomFactor = (Math.random() - 0.5) * 2; // -1 to 1
        const actionEffect = Math.floor(repCount * actionMultiplier * randomFactor);

        switch (action) {
            case 'attack':
                attackChange += actionEffect;
                break;
            case 'defend':
                defendChange += actionEffect;
                break;
            case 'run':
                workoutChange += actionEffect;
                break;
        }

        const result = {
            attack: attackChange,
            defend: defendChange,
            run: workoutChange,
            move: selectedMove.move,
            action: action,
            outcome: getOutcomeMessage(attackChange, defendChange, workoutChange)
        };

        setBattleResult(result);
        setCurrentPhase('battle-result');

        // 🎵 PLAY BATTLE OUTCOME SOUND after a brief delay
        setTimeout(() => {
            soundManager.playBattleOutcome(result.outcome);
        }, 500);

        setTimeout(() => setShowContinueButton(true), 2000);
    };

    const getActionMultiplier = (action) => {
        switch (action) {
            case 'attack': return 2;
            case 'defend': return 1;
            case 'run': return 1.5;
            default: return 1;
        }
    };

    const getOutcomeMessage = (attack, defend, run) => {
        const total = attack + defend + run;
        if (total > 10) return "💥 CRUSHING VICTORY! 💥";
        if (total > 5) return "⚔️ Victory! You defeated the villain!";
        if (total > 0) return "🤺 Close battle, but you survived!";
        if (total > -5) return "😰 You barely escaped!";
        if (total > -10) return "💀 The villain dealt heavy damage!";
        return "☠️ DEVASTATING DEFEAT! ☠️";
    };

    const handleContinue = () => {
        // 🎵 CONTINUE BUTTON SOUND
        soundManager.playButtonSound();
        onVillainComplete(battleResult);
    };

    // Get villain background - use the villain's specific image
    const getVillainBackground = () => {
        // Use the villain's specific image if available, otherwise random
        const villainImageIndex = villain.image ? villain.image.replace('v', '').replace('.jpg', '') : Math.floor(Math.random() * 88) + 1;
        return `url(/assets/images/v${villainImageIndex}.jpg)`;
    };

    return (
        <div
            className="villain-encounter-container"
            style={{
                backgroundImage: getVillainBackground(),
                backgroundSize: 'cover',
                backgroundPosition: 'center'
            }}
        >
            {/* Animated battle overlay that fades to reveal villain */}
            <div
                className="villain-overlay"
                style={{
                    opacity: overlayOpacity,
                    transition: 'opacity 1.5s ease-out'
                }}
            ></div>

            {/* Battle stats HUD */}
            <div className="villain-hud">
                <div className="villain-stats-display">
                    <div className="stat-circle attack">
                        <span className="stat-value">{battleStats.attack}</span>
                        <span className="stat-label">ATK</span>
                    </div>
                    <div className="stat-circle defend">
                        <span className="stat-value">{battleStats.defend}</span>
                        <span className="stat-label">DEF</span>
                    </div>
                    <div className="stat-circle run">
                        <span className="stat-value">{battleStats.run}</span>
                        <span className="stat-label">RUN</span>
                    </div>
                    <div className="stat-circle current-reps">
                        <span className="stat-value">{repCount}</span>
                        <span className="stat-label">REPS</span>
                    </div>
                </div>
            </div>

            <div className="villain-content">
                {/* Villain intro phase */}
                {currentPhase === 'intro' && (
                    <div className="villain-intro">
                        <div className="villain-appear-animation">
                            <h1 className="villain-name">{villain.name}</h1>
                            <div className="villain-threat">💀 A WILD VILLAIN APPEARS! 💀</div>
                            <div className="villain-warning">Prepare for battle...</div>
                            <div className="villain-image-indicator">
                                <span className="image-hint">🖼️ Villain image revealing...</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Move selection phase */}
                {currentPhase === 'move-selection' && (
                    <div className="move-selection-phase">
                        <div className="villain-name-header">{villain.name}</div>
                        <div className="battle-prompt">
                            <h2>⚔️ You have one move, and one action ⚔️</h2>
                            <p>Choose your bodyweight move to counter the villain!</p>
                        </div>

                        <div className="moves-grid">
                            {villain.moveOptions.map((move, index) => (
                                <button
                                    key={index}
                                    className="move-button"
                                    onClick={() => handleMoveSelection(move, index)}
                                >
                                    <span className="move-name">{move}</span>
                                    <span className="move-hint">💪</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Action selection phase */}
                {currentPhase === 'action-selection' && (
                    <div className="action-selection-phase">
                        <div className="villain-name-header">{villain.name}</div>
                        <div className="selected-move-display">
                            <h3>Selected Move: <span className="selected-move">{selectedMove.move}</span></h3>
                            <p>Now choose your battle action!</p>
                        </div>

                        <div className="actions-grid">
                            <button
                                className="action-button attack"
                                onClick={() => handleActionSelection('attack')}
                            >
                                <span className="action-icon">⚔️</span>
                                <span className="action-name">ATTACK</span>
                                <span className="action-desc">High risk, high reward</span>
                            </button>

                            <button
                                className="action-button defend"
                                onClick={() => handleActionSelection('defend')}
                            >
                                <span className="action-icon">🛡️</span>
                                <span className="action-name">DEFEND</span>
                                <span className="action-desc">Safer protection</span>
                            </button>

                            <button
                                className="action-button run"
                                onClick={() => handleActionSelection('run')}
                            >
                                <span className="action-icon">🏃‍♂️</span>
                                <span className="action-name">RUN</span>
                                <span className="action-desc">Unpredictable chaos</span>
                            </button>
                        </div>
                    </div>
                )}

                {/* Battle result phase */}
                {currentPhase === 'battle-result' && battleResult && (
                    <div className="battle-result-phase">
                        <div className="villain-name-header">{villain.name}</div>

                        <div className="battle-summary">
                            <h2>⚡ BATTLE COMPLETE! ⚡</h2>
                            <div className="result-outcome">{battleResult.outcome}</div>

                            <div className="battle-details">
                                <div className="battle-choice">
                                    <span className="choice-label">Your Move:</span>
                                    <span className="choice-value">{battleResult.move}</span>
                                </div>
                                <div className="battle-choice">
                                    <span className="choice-label">Your Action:</span>
                                    <span className="choice-value">{battleResult.action.toUpperCase()}</span>
                                </div>
                            </div>

                            <div className="stat-changes">
                                <div className="stat-change">
                                    <span className="change-label">Attack:</span>
                                    <span className={`change-value ${battleResult.attack >= 0 ? 'positive' : 'negative'}`}>
                                        {battleResult.attack >= 0 ? '+' : ''}{battleResult.attack}
                                    </span>
                                </div>
                                <div className="stat-change">
                                    <span className="change-label">Defend:</span>
                                    <span className={`change-value ${battleResult.defend >= 0 ? 'positive' : 'negative'}`}>
                                        {battleResult.defend >= 0 ? '+' : ''}{battleResult.defend}
                                    </span>
                                </div>
                                <div className="stat-change">
                                    <span className="change-label">Run:</span>
                                    <span className={`change-value ${battleResult.run >= 0 ? 'positive' : 'negative'}`}>
                                        {battleResult.run >= 0 ? '+' : ''}{battleResult.run}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {showContinueButton && (
                            <button
                                className="continue-battle-button"
                                onClick={handleContinue}
                            >
                                <span className="continue-text">⚔️ Continue Battle</span>
                                <span className="continue-sub">Press on, warrior!</span>
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default VillainEncounter;